<template>
  <div class="search page">
    <div class="container p-0">
      <h1 class="custom-title mt-4">Search</h1>

      <b-form @submit.prevent="handleSearch">
        <b-input-group class="search__input mt-5">
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="custom-form-input"
            @input="handleSearch"
          />
          <b-input-group-append class="search__input-icon">
            <img
              src="@/assets/icons/search-purple.svg"
              alt="close"
              class="mx-3"
              @click="handleSearch"
            />
          </b-input-group-append>
        </b-input-group>
      </b-form>

      <CreatorCarousel
        v-if="!isSearchMode"
        id="discoverCreators"
        class="mb-5 mt-5"
        :items="creators"
        @click="handleCreatorClick"
      >
        <template #title>
          <h2>Discover creators</h2>
        </template>
      </CreatorCarousel>

      <SearchBlock
        v-else
        class="mt-5"
        :items="result"
        :count-showed-items="countShowedItems"
        :loading="isLoading"
        :view-more-loading="isViewMoreLoading"
        :totals="totals"
        @load-more="handleViewMoreClick"
        @reset-count="resetCountShowedItems"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';

import { feedTypes } from '@/components/TrendingBlock/constants';
import SearchBlock from '@/components/SearchBlock';

import { search } from '@/services/selections';
import { fetchCreatorsTrending } from '@/services/statistics';

export default {
  name: 'SearchComponent',

  components: {
    SearchBlock,
    CreatorCarousel: () =>
      import(/* webpackPrefetch: true */ '@/components/CreatorCarousel')
  },

  data() {
    return {
      isSearchMode: false,
      searchTerm: '',
      countShowedItems: 10,
      limit: 10,
      skip: 0,
      result: [],
      totals: {
        creators: 0,
        products: 0,
        recipes: 0
      },
      isLoading: false,
      isViewMoreLoading: false,
      creators: []
    };
  },

  head: {
    title: 'Search | Found'
  },

  mounted() {
    const initialSearchTerm = this.$get(this.$route, 'query.keyword', null);
    if (initialSearchTerm) {
      this.searchTerm = initialSearchTerm;
      this.isSearchMode = true;
      this.isLoading = true;
      this.searchContent();
    }
    fetchCreatorsTrending(this.$axios).then((response) => {
      this.creators = response?.data;
    });
  },

  methods: {
    handleCreatorClick(creator) {
      this.$router.push({
        path: `/creators/${
          this.$get(creator, 'profile.handle') ||
          this.$get(creator, 'item.profile.handle') ||
          this.$get(creator, 'id')
        }`
      });
    },
    handleViewMoreClick(filter) {
      this.countShowedItems += this.limit;
      const filteredItems =
        filter === feedTypes.ALL
          ? this.result
          : this.result.filter((item) => item.entity === filter);

      if (this.countShowedItems > filteredItems.length) {
        this.isViewMoreLoading = true;
        this.searchContent();
      }
    },
    prepareData(response, isShowMore) {
      const newItems = this.$get(response, 'data', []);
      this.result = isShowMore ? this.result.concat(newItems) : newItems;
      this.totals = response.totals;
    },
    clearSearchResults() {
      this.skip = 0;
      this.result = [];
      this.resetCountShowedItems();
    },
    resetCountShowedItems() {
      this.countShowedItems = this.limit;
    },
    handleSearch: debounce(function () {
      if (this.searchTerm) {
        this.isSearchMode = true;
        this.isLoading = true;
        this.clearSearchResults();
        this.searchContent();
        this.$router.replace({
          query: { keyword: this.searchTerm }
        });
      } else {
        this.isSearchMode = false;
        this.isLoading = false;
        this.$router
          .replace({
            query: null
          })
          .catch(() => {});
      }
    }, 1500),
    searchContent() {
      const query = {
        $search: this.searchTerm,
        $limit: this.limit,
        $skip: this.skip
      };
      search(this.$axios, { query }).then((response) => {
        this.prepareData(response, true);
        this.skip += this.limit;
        this.isLoading = false;
        this.isViewMoreLoading = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../styles/components';

.search {
  &__input {
    border-radius: 10px;
    border: 1px solid $grey-color;

    &:focus {
      border-color: $primary-color;
      box-shadow: 0 0 0 0.2rem rgb(146 87 224 / 25%);
    }

    .custom-form-input {
      border: none;
      z-index: 1;
      border-radius: 12px;

      &:focus {
        border: none;
        box-shadow: none;
      }
    }

    &-icon {
      align-items: center;

      img {
        height: 25px;
        width: 25px;

        &:hover {
          cursor: pointer;
        }
      }
    }
  }
}
</style>
